import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer, useJsApiLoader, MarkerClusterer } from "@react-google-maps/api";
// import { Clusterer } from "@react-google-maps/marker-clusterer";
import { Image } from 'cloudinary-react';
import { Button } from 'react-onsenui';
import OrderDrinkForm from "./drink/OrderDrinkForm";
import * as dayjs from 'dayjs';
import DirectionsCar from '@mui/icons-material/DirectionsCar';

const libraries = ["places"];

const Map = ({ markers, draggable, position, setSelectedShop, pushPage, userBeanIds }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY,
    libraries,
  });

  const [center, setCenter] = useState({ lat: -36.848461, lng: 174.763336 });
  const [zoom, setZoom] = useState(12);
  const [cardOpen, setCardOpen] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(position);
  const [directions, setDirections] = useState(null);
  const mapRef = useRef(null);

  // Define findClosestMarker inside the component
  const findClosestMarker = (markers, lat1, lon1) => {
    const pi = Math.PI;
    const R = 6371;
    let distances = [];
    let closest = -1;

    for (let i = 0; i < markers.length; i++) {
      const lat2 = markers[i].lat;
      const lon2 = markers[i].lng;

      const chLat = lat2 - lat1;
      const chLon = lon2 - lon1;

      const dLat = chLat * (pi / 180);
      const dLon = chLon * (pi / 180);

      const rLat1 = lat1 * (pi / 180);
      const rLat2 = lat2 * (pi / 180);

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(rLat1) * Math.cos(rLat2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const d = R * c;

      distances[i] = d;
      if (closest === -1 || d < distances[closest]) {
        closest = i;
      }
    }

    return markers[closest];
  };

  useEffect(() => {
    if (position) {
      setCurrentLocation(position);
      setCenter({ lat: position.lat, lng: position.lng });
      setZoom(15);
    }
  }, [position]);

  useEffect(() => {
    if (markers.length > 0 && mapRef.current) {
      resetBounds();
    }
  }, [markers]);

  const resetBounds = () => {
    if (!window.google || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();

    if (currentLocation) {
      const positionLatLng = new window.google.maps.LatLng(currentLocation.lat, currentLocation.lng);
      bounds.extend(positionLatLng);

      const closest = findClosestMarker(markers, currentLocation.lat, currentLocation.lng);
      const closestLatLng = new window.google.maps.LatLng(closest.lat, closest.lng);
      bounds.extend(closestLatLng);
    } else {
      markers.forEach(marker => {
        const latLng = new window.google.maps.LatLng(marker.lat, marker.lng);
        bounds.extend(latLng);
      });
    }

    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      const extra = 0.001;
      const extendPoint1 = new window.google.maps.LatLng(bounds.getNorthEast().lat() + extra, bounds.getNorthEast().lng() + extra);
      const extendPoint2 = new window.google.maps.LatLng(bounds.getNorthEast().lat() - extra, bounds.getNorthEast().lng() - extra);
      bounds.extend(extendPoint1);
      bounds.extend(extendPoint2);
    }

    mapRef.current.fitBounds(bounds);
  };

  const handleOrderClicked = (id) => {
    setSelectedShop(id);
    pushPage(OrderDrinkForm, 'order-form', { shopId: id });
  };

  const handleDirectionsClicked = (currentLocation, destination) => {
    if (!window.google) return;

    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route({
      origin: new window.google.maps.LatLng(currentLocation.lat, currentLocation.lng),
      destination: new window.google.maps.LatLng(destination.lat, destination.lng),
      travelMode: window.google.maps.TravelMode.WALKING,
    }, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
      } else {
        console.error(`error fetching directions ${result}`);
      }
    });
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      mapContainerStyle={{ height: "100%", width: "100%" }}
      center={center}
      zoom={zoom}
      options={{
        streetViewControl: false,
        disableDefaultUI: true,
        styles: [
          {
            featureType: "landscape.man_made",
            elementType: "geometry.fill",
            stylers: [{ color: "#eee5da" }],
          },
          {
            featureType: "landscape.natural",
            elementType: "geometry.fill",
            stylers: [{ color: "#eee5da" }],
          },
          {
            featureType: "landscape.natural.landcover",
            stylers: [{ color: "#bea380" }],
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      }}
      onLoad={(map) => (mapRef.current = map)}
    >
      <DirectionsRenderer
        directions={directions}
        options={{
          polylineOptions: {
            strokeColor: '#000',
            strokeWeight: 5,
          },
        }}
      />

      {currentLocation && (
        <Marker
          key="currentLocation"
          position={currentLocation}
          icon="/images/current-location.png"
        />
      )}

      <MarkerClusterer>
        {(clusterer) =>
          markers.map((marker, index) => {
            const weekday = dayjs().format('dddd').toLowerCase();
            let isOpen = marker[`open_${weekday}`];

            if (isOpen && marker[`opening_time_${weekday}`] && marker[`closing_time_${weekday}`]) {
              const now = dayjs();
              const openingTime = dayjs(marker[`opening_time_${weekday}`], 'HH:mm:ss');
              const closingTime = dayjs(marker[`closing_time_${weekday}`], 'HH:mm:ss').subtract(15, 'minutes');

              if (now.isBefore(openingTime) || now.isAfter(closingTime)) {
                isOpen = false;
              }
            }

            return (
              <Marker
                key={index}
                position={{ lat: Number(marker.lat), lng: Number(marker.lng) }}
                icon={marker.beans.length > 0 && userBeanIds.includes(marker.beans[0].id) ? "/images/marker_black.png" : "/images/marker_black.png"}
                onClick={() => setCardOpen(index)}
                clusterer={clusterer}
              >
                {cardOpen === index && (
                  <InfoWindow onCloseClick={() => setCardOpen(null)}>
                    <div className="c-map__card">
                      {marker.banner && (
                        <Image
                          cloudName={process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}
                          publicId={marker.banner}
                          className="c-map__card-image"
                        />
                      )}

                      <div className="c-map__card-content">
                        <div className="c-map__card-title">
                          <h4>{marker.name}</h4>
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${marker.lat},${marker.lng}`} target="_blank" rel="noreferrer">
                            <DirectionsCar className="u-width-30" />
                          </a>
                        </div>

                        {marker.beans.length > 0 && userBeanIds.includes(marker.beans[0].id) && (
                          <p className="c-cafelist__bean">{marker.beans[0].name}</p>
                        )}

                        <p>{marker.description}</p>

                        {!marker.open && (
                          <p className="c-cafelist__item__temporarily-closed">
                            Sorry, we are temporarily not taking orders, we'll be back soon!
                          </p>
                        )}

                        <p>{marker.address}</p>

                        <div className="u-d-flex u-mt-15">
                          <Button
                            className="u-width-40 u-text-center u-mr-05"
                            disabled={!isOpen || !marker.open}
                            onClick={() => handleOrderClicked(marker.id)}
                          >
                            {isOpen && marker.open ? 'Order' : 'Closed'}
                          </Button>

                          {currentLocation && (
                            <Button
                              className="u-text-center u-width-60"
                              disabled={!isOpen || !marker.open}
                              onClick={() => handleDirectionsClicked(currentLocation, marker)}
                            >
                              Directions
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })
        }
      </MarkerClusterer>
    </GoogleMap>
  );
};

export default Map;